import React, { useEffect, useState } from 'react';
import { X, Info, ChevronDown, ChevronUp, Check, Calendar, Clock } from 'lucide-react';
interface ScheduleReportsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (schedule: any) => void;
  initialData?: any;
}
export function ScheduleReportsModal({
  isOpen,
  onClose,
  onSave,
  initialData
}: ScheduleReportsModalProps) {
  const [reportType, setReportType] = useState('Member Summary Report');
  const [frequency, setFrequency] = useState('Weekly');
  const [dayOfWeek, setDayOfWeek] = useState('Monday');
  const [dayOfMonth, setDayOfMonth] = useState('1st');
  const [timeHour, setTimeHour] = useState('8');
  const [timeAmPm, setTimeAmPm] = useState('AM');
  const [recipients, setRecipients] = useState<string[]>(['user@example.com']);
  const [newRecipient, setNewRecipient] = useState('');
  const [recipientError, setRecipientError] = useState('');
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [includeSummary, setIncludeSummary] = useState(true);
  const [includeComparison, setIncludeComparison] = useState(true);
  const [includeDetails, setIncludeDetails] = useState(false);
  const [fileFormat, setFileFormat] = useState('CSV');
  const [isSaving, setIsSaving] = useState(false);
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        // Populate from initialData if editing
        setReportType(initialData.reportType || 'Member Summary Report');
        setFrequency(initialData.frequency || 'Weekly');
        // ... set other fields
      } else {
        // Reset to defaults
        setReportType('Member Summary Report');
        setFrequency('Weekly');
        setDayOfWeek('Monday');
        setDayOfMonth('1st');
        setTimeHour('8');
        setTimeAmPm('AM');
        setRecipients(['user@example.com']); // In real app, use current user email
        setNewRecipient('');
        setRecipientError('');
        setIsAdvancedOpen(false);
        setIncludeSummary(true);
        setIncludeComparison(true);
        setIncludeDetails(false);
        setFileFormat('CSV');
      }
      setIsSaving(false);
    }
  }, [isOpen, initialData]);
  if (!isOpen) return null;
  const handleAddRecipient = (e?: React.KeyboardEvent) => {
    if (e && e.key !== 'Enter') return;
    e?.preventDefault();
    const email = newRecipient.trim();
    if (!email) return;
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setRecipientError('Please enter a valid email address');
      return;
    }
    if (recipients.length >= 5) {
      setRecipientError('Maximum 5 recipients allowed');
      return;
    }
    if (recipients.includes(email)) {
      setRecipientError('Email already added');
      return;
    }
    setRecipients([...recipients, email]);
    setNewRecipient('');
    setRecipientError('');
  };
  const removeRecipient = (emailToRemove: string) => {
    setRecipients(recipients.filter(email => email !== emailToRemove));
  };
  const handleSave = () => {
    if (recipients.length === 0) {
      setRecipientError('At least one recipient is required');
      return;
    }
    setIsSaving(true);
    // Simulate API call
    setTimeout(() => {
      onSave({
        reportType,
        frequency,
        schedule: {
          dayOfWeek: frequency === 'Weekly' ? dayOfWeek : undefined,
          dayOfMonth: frequency === 'Monthly' || frequency === 'Quarterly' ? dayOfMonth : undefined,
          time: `${timeHour}:00 ${timeAmPm}`
        },
        recipients,
        options: {
          includeSummary,
          includeComparison,
          includeDetails,
          fileFormat
        }
      });
      setIsSaving(false);
      onClose();
    }, 1500);
  };
  const getReportDescription = () => {
    switch (reportType) {
      case 'Member Summary Report':
        return 'Includes member name, email, subscription status, tier, join date, lifetime value, and last order date.';
      case 'Revenue Report':
        return 'Detailed breakdown of revenue by product, subscription tier, and customer segments over time.';
      case 'Payment Issues Report':
        return 'List of all failed payments, retry attempts, and at-risk subscriptions requiring attention.';
      case 'Cancellation Report':
        return 'Analysis of cancelled subscriptions including reasons, churn dates, and customer tenure.';
      default:
        return 'Standard report containing key metrics and data points for the selected category.';
    }
  };
  return <div className="fixed inset-0 z-[1050] flex items-center justify-center" data-id="element-2432">
      {/* Overlay */}
      <div className="absolute inset-0 bg-[#0F172A]/50 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-2433" />

      {/* Modal Container */}
      <div className="relative w-full max-w-[560px] bg-white rounded-lg shadow-[0_20px_25px_rgba(15,23,42,0.15)] flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200" data-id="element-2434">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-[#E2E8F0] flex-shrink-0" data-id="element-2435">
          <div className="flex items-start justify-between" data-id="element-2436">
            <div data-id="element-2437">
              <h2 className="text-[20px] font-semibold text-[#0F172A] leading-tight" data-id="element-2438">
                {initialData ? 'Edit Scheduled Report' : 'Schedule Report'}
              </h2>
              <p className="text-[13px] text-[#64748B] mt-1" data-id="element-2439">
                Automatically receive reports in your inbox
              </p>
            </div>
            <button onClick={onClose} className="p-1.5 text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-md transition-colors" aria-label="Close modal" data-id="element-2440">
              <X className="w-5 h-5" data-id="element-2441" />
            </button>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6" data-id="element-2442">
          {/* Section 1: Report Type */}
          <div data-id="element-2443">
            <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2444">
              Report Type
            </label>
            <div className="relative" data-id="element-2445">
              <select value={reportType} onChange={e => setReportType(e.target.value)} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none transition-all appearance-none" disabled={isSaving} data-id="element-2446">
                <option data-id="element-2447">Member Summary Report</option>
                <option data-id="element-2448">Revenue Report</option>
                <option data-id="element-2449">Payment Issues Report</option>
                <option data-id="element-2450">Cancellation Report</option>
                <option data-id="element-2451">Subscription Activity Report</option>
                <option data-id="element-2452">Churn Analysis Report</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8] pointer-events-none" data-id="element-2453" />
            </div>
            <div className="mt-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-3 flex gap-2" data-id="element-2454">
              <Info className="w-4 h-4 text-[#94A3B8] flex-shrink-0 mt-0.5" data-id="element-2455" />
              <p className="text-[13px] text-[#64748B] leading-relaxed" data-id="element-2456">
                {getReportDescription()}
              </p>
            </div>
          </div>

          {/* Section 2: Frequency */}
          <div data-id="element-2457">
            <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2458">
              Frequency
            </label>
            <div className="flex flex-wrap gap-2 mb-4" data-id="element-2459">
              {['Daily', 'Weekly', 'Monthly', 'Quarterly'].map(option => <button key={option} onClick={() => setFrequency(option)} disabled={isSaving} className={`
                    h-10 px-4 rounded-md text-[14px] font-medium transition-all border
                    ${frequency === option ? 'bg-[#E0F2FE] border-[#0EA5E9] text-[#0EA5E9]' : 'bg-white border-[#CBD5E1] text-[#475569] hover:bg-[#F8FAFC]'}
                  `} data-id="element-2460">
                  {option}
                </button>)}
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-2461">
              {/* Conditional Frequency Fields */}
              {frequency === 'Weekly' && <div data-id="element-2462">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2463">
                    Day of Week
                  </label>
                  <div className="relative" data-id="element-2464">
                    <select value={dayOfWeek} onChange={e => setDayOfWeek(e.target.value)} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none" disabled={isSaving} data-id="element-2465">
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => <option key={day} value={day} data-id="element-2466">
                          {day}
                        </option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8] pointer-events-none" data-id="element-2467" />
                  </div>
                </div>}

              {(frequency === 'Monthly' || frequency === 'Quarterly') && <div data-id="element-2468">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2469">
                    Day of Month
                  </label>
                  <div className="relative" data-id="element-2470">
                    <select value={dayOfMonth} onChange={e => setDayOfMonth(e.target.value)} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none" disabled={isSaving} data-id="element-2471">
                      {Array.from({
                    length: 28
                  }, (_, i) => i + 1).map(day => <option key={day} value={`${day}${getOrdinal(day)}`} data-id="element-2472">
                          {day}
                          {getOrdinal(day)}
                        </option>)}
                      <option value="Last day" data-id="element-2473">Last day</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8] pointer-events-none" data-id="element-2474" />
                  </div>
                </div>}

              {/* Time Selection - Always visible */}
              <div className={frequency === 'Daily' ? 'col-span-2' : 'col-span-1'} data-id="element-2475">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2476">
                  Time
                </label>
                <div className="flex gap-2" data-id="element-2477">
                  <div className="relative flex-1" data-id="element-2478">
                    <select value={timeHour} onChange={e => setTimeHour(e.target.value)} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none" disabled={isSaving} data-id="element-2479">
                      {Array.from({
                      length: 12
                    }, (_, i) => i + 1).map(hour => <option key={hour} value={hour} data-id="element-2480">
                          {hour}:00
                        </option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8] pointer-events-none" data-id="element-2481" />
                  </div>
                  <div className="relative w-[80px]" data-id="element-2482">
                    <select value={timeAmPm} onChange={e => setTimeAmPm(e.target.value)} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none" disabled={isSaving} data-id="element-2483">
                      <option value="AM" data-id="element-2484">AM</option>
                      <option value="PM" data-id="element-2485">PM</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8] pointer-events-none" data-id="element-2486" />
                  </div>
                </div>
              </div>
            </div>

            {frequency === 'Quarterly' && <p className="text-[12px] text-[#64748B] mt-2 bg-[#F8FAFC] px-3 py-2 rounded border border-[#E2E8F0]" data-id="element-2487">
                Reports sent in January, April, July, October
              </p>}

            <p className="text-[12px] text-[#94A3B8] mt-2" data-id="element-2488">
              Reports are generated in your store's timezone (AEST)
            </p>
          </div>

          {/* Section 3: Recipients */}
          <div data-id="element-2489">
            <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2490">
              Send to
            </label>
            <div className={`
                min-h-10 px-2 py-1 bg-white border rounded-md flex flex-wrap gap-2 items-center
                focus-within:border-[#0EA5E9] focus-within:ring-2 focus-within:ring-[#0EA5E9]/20 transition-all
                ${recipientError ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}
              `} data-id="element-2491">
              {recipients.map(email => <span key={email} className="inline-flex items-center gap-1 px-2 py-1 bg-[#E0F2FE] text-[#0284C7] text-[13px] rounded-md" data-id="element-2492">
                  {email}
                  <button onClick={() => removeRecipient(email)} className="hover:text-[#0369A1] focus:outline-none" disabled={isSaving} data-id="element-2493">
                    <X className="w-3.5 h-3.5" data-id="element-2494" />
                  </button>
                </span>)}
              <input type="text" value={newRecipient} onChange={e => {
              setNewRecipient(e.target.value);
              setRecipientError('');
            }} onKeyDown={handleAddRecipient} onBlur={() => {
              if (newRecipient) handleAddRecipient();
            }} placeholder={recipients.length === 0 ? 'Enter email addresses...' : ''} className="flex-1 min-w-[120px] h-8 outline-none text-[14px] text-[#0F172A] placeholder:text-[#94A3B8]" disabled={isSaving} data-id="element-2495" />
            </div>
            {recipientError && <p className="text-[12px] text-[#EF4444] mt-1" data-id="element-2496">
                {recipientError}
              </p>}
            <p className="text-[12px] text-[#94A3B8] mt-1" data-id="element-2497">
              Press Enter to add multiple recipients
            </p>
          </div>

          {/* Section 4: Advanced Options */}
          <div className="border-t border-[#E2E8F0] pt-4" data-id="element-2498">
            <button onClick={() => setIsAdvancedOpen(!isAdvancedOpen)} className="flex items-center gap-2 text-[14px] font-medium text-[#475569] hover:text-[#0F172A] transition-colors" type="button" data-id="element-2499">
              {isAdvancedOpen ? <ChevronUp className="w-4 h-4" data-id="element-2500" /> : <ChevronDown className="w-4 h-4" data-id="element-2501" />}
              Advanced Options
            </button>

            {isAdvancedOpen && <div className="mt-4 space-y-4 animate-in slide-in-from-top-2 duration-200" data-id="element-2502">
                <div className="space-y-3" data-id="element-2503">
                  <label className="flex items-center gap-3 cursor-pointer group" data-id="element-2504">
                    <input type="checkbox" checked={includeSummary} onChange={e => setIncludeSummary(e.target.checked)} className="w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] cursor-pointer" disabled={isSaving} data-id="element-2505" />
                    <span className="text-[14px] text-[#475569] group-hover:text-[#0F172A]" data-id="element-2506">
                      Include summary statistics at top
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer group" data-id="element-2507">
                    <input type="checkbox" checked={includeComparison} onChange={e => setIncludeComparison(e.target.checked)} className="w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] cursor-pointer" disabled={isSaving} data-id="element-2508" />
                    <span className="text-[14px] text-[#475569] group-hover:text-[#0F172A]" data-id="element-2509">
                      Include comparison to previous period
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer group" data-id="element-2510">
                    <input type="checkbox" checked={includeDetails} onChange={e => setIncludeDetails(e.target.checked)} className="w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] cursor-pointer" disabled={isSaving} data-id="element-2511" />
                    <span className="text-[14px] text-[#475569] group-hover:text-[#0F172A]" data-id="element-2512">
                      Include individual member details
                    </span>
                  </label>
                </div>

                <div className="pt-2" data-id="element-2513">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2514">
                    File Format
                  </label>
                  <div className="flex gap-4" data-id="element-2515">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2516">
                      <input type="radio" name="fileFormat" value="CSV" checked={fileFormat === 'CSV'} onChange={e => setFileFormat(e.target.value)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" disabled={isSaving} data-id="element-2517" />
                      <span className="text-[14px] text-[#475569]" data-id="element-2518">CSV</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2519">
                      <input type="radio" name="fileFormat" value="Excel" checked={fileFormat === 'Excel'} onChange={e => setFileFormat(e.target.value)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" disabled={isSaving} data-id="element-2520" />
                      <span className="text-[14px] text-[#475569]" data-id="element-2521">
                        Excel (.xlsx)
                      </span>
                    </label>
                  </div>
                </div>
              </div>}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-[#F8FAFC] border-t border-[#E2E8F0] rounded-b-lg flex justify-between items-center flex-shrink-0" data-id="element-2522">
          <div data-id="element-2523">
            {initialData && <button className="text-[14px] font-medium text-[#EF4444] hover:text-[#DC2626] transition-colors" disabled={isSaving} data-id="element-2524">
                Delete Schedule
              </button>}
          </div>
          <div className="flex gap-3" data-id="element-2525">
            <button onClick={onClose} className="h-10 px-5 border border-[#CBD5E1] rounded-md text-[14px] font-medium text-[#475569] hover:bg-white hover:border-[#94A3B8] transition-all bg-white" disabled={isSaving} data-id="element-2526">
              Cancel
            </button>
            <button onClick={handleSave} disabled={isSaving} className="h-10 px-5 bg-[#0EA5E9] rounded-md text-[14px] font-medium text-white hover:bg-[#0284C7] transition-all shadow-sm flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-2527">
              {isSaving ? <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2528" />
                  Saving...
                </> : <>{initialData ? 'Update Schedule' : 'Save Schedule'}</>}
            </button>
          </div>
        </div>
      </div>
    </div>;
}
// Helper for ordinal suffixes
function getOrdinal(n: number) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}